import React, { useContext as useContextCore } from 'react'
import type { BreadcrumbItem } from '@ztechapps/webui.page'
import type { ConversationProviderProps } from './Conversation/Context'

/**
 * Product-shell values the conversation UI reads but must not import: route URLs and
 * environment URLs. The hosting app provides them once (PanelPunches reads routesDef and
 * app-settings); a second product provides its own. Nothing in this package imports app code.
 */
export type ConversationHostConfig = {
  /** Builds the inbox-flyout URL for a conversation external id (list route + id). */
  conversationFlyoutUrl: (externalId: string) => string
  /** Breadcrumb entries for the overview and inbox routes. */
  overviewRoute: BreadcrumbItem
  conversationRoute: BreadcrumbItem
  /** Passed through to the conversation provider (unread downstream). */
  rootUrl?: string
  /** Passed through to the conversation provider (no reader today). */
  storeUrl?: string
  /** Template categories the inbox picker offers — one per record domain, owned by the host app. */
  communicationTemplateCategories: string[]
  /** Picker the inbox composers mount — the same component the record cards mount. */
  templateSelectComponent: NonNullable<ConversationProviderProps['templateSelectComponent']>
  /** The host binds `WebuiIocKey.order.orderSelect`, so the new-conversation composer offers "Link to order". Defaults to true. */
  hasOrderSelect?: boolean
  /** The host binds `WebuiIocKey.customer.customerInfoForm`, so the customer popover offers the "Edit Info" tab. Defaults to true. */
  hasCustomerInfoForm?: boolean
  /** Inbox list columns the host has no data for, by their `conversationListFieldConfig` key — the inbox omits them. */
  hiddenInboxColumns?: string[]
}

const context = React.createContext<ConversationHostConfig>({} as ConversationHostConfig)

export type ConversationHostProviderProps = {
  routes: ConversationHostConfig
  children: React.ReactNode
}

export const ConversationHostProvider = ({ routes, children }: ConversationHostProviderProps) => {
  return <context.Provider value={routes}>{children}</context.Provider>
}

export const useConversationHost = () => useContextCore(context)
